import { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import holyartExhibition5 from '@/assets/holyart-exhibition-5.jpg';
import holyartExhibition6 from '@/assets/holyart-exhibition-6.jpg';
import holyartExhibition7 from '@/assets/holyart-exhibition-7.jpg';
import holyartExhibition8 from '@/assets/holyart-exhibition-8.jpg';
import earthenEchoes1 from '@/assets/earthen-echoes-1.jpg';
import earthenEchoesPoster from '@/assets/earthen-echoes-poster.jpg';
import kartscon2 from '@/assets/kartscon-2.png.asset.json';
import kartscon3 from '@/assets/kartscon-3.png.asset.json';
import kartscon4 from '@/assets/kartscon-4.png.asset.json';
import kartscon5 from '@/assets/kartscon-5.png.asset.json';
import kartscon6 from '@/assets/kartscon-6.png.asset.json';
import kartscon7 from '@/assets/kartscon-7.png.asset.json';
import kartscon8 from '@/assets/kartscon-8.png.asset.json';
import kartscon9 from '@/assets/kartscon-9.png.asset.json';
import kartscon10 from '@/assets/kartscon-10.png.asset.json';
import kartscon11 from '@/assets/kartscon-11.png.asset.json';

// Featured exhibition with full description
const featuredExhibition = {
  id: 'earthen-echoes',
  title: 'Earthen Echoes, Solo Exhibition (2021)',
  images: [earthenEchoesPoster, earthenEchoes1],
  location: 'Orchard Light Gallery, Ilorin, Nigeria',
  date: 'April 17 - 24, 2021',
  description: `Earthen Echoes was my first solo exhibition, held at Orchard Light Gallery in Ilorin, Nigeria, from April 17 to 24, 2021.

This show came at a really important moment in my journey as a ceramic artist. It was the first time I presented a full body of work to the public on my own.

The pieces in this exhibition were created over several months of experimentation and reflection. I was deeply interested in how clay can hold memory, emotion and cultural meaning, and I wanted the works to move between being functional objects and sculptural forms. Some pieces were quiet and intimate, while others felt bold and grounded, but all of them came from the same desire to understand myself and my environment better through material.

Seeing the works installed together in the gallery helped me understand my practice in a new way. Earthen Echoes became more than just an exhibition. It became a turning point that shaped how I think about my work, my process and my future as an artist.`,
};

// Multi-image exhibitions (clickable galleries)
const galleryExhibitions = [
  {
    id: 'affordable-art',
    images: [holyartExhibition5, holyartExhibition6, holyartExhibition7, holyartExhibition8],
    name: '',
    location: '',
    year: '',
  },
  {
    id: 'kent-arts-conference',
    images: [kartscon2.url, kartscon3.url, kartscon4.url, kartscon5.url, kartscon6.url],
    name: 'Kent Arts Conference',
    location: 'Kent, United Kingdom',
    year: '2026',
  },
  {
    id: 'cupola-35',
    images: [kartscon8.url, kartscon7.url, kartscon9.url],
    name: '35 Year Anniversary Show',
    location: 'Cupola Contemporary Gallery, Sheffield, United Kingdom',
    year: '2026',
  },
  {
    id: 'studio-collection',
    images: [kartscon10.url, kartscon11.url],
    name: 'Studio Collection',
    location: 'London, United Kingdom',
    year: '2026',
  },
];

interface Gallery {
  id: string;
  images: string[];
  name: string;
  location: string;
  year: string;
}

const Exhibitions = () => {
  const [activeGallery, setActiveGallery] = useState<Gallery | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const nextImage = () => {
    if (!activeGallery) return;
    setCurrentImageIndex((prev) => 
      prev === activeGallery.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    if (!activeGallery) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? activeGallery.images.length - 1 : prev - 1
    );
  };

  return (
    <div className="py-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-20">
        <div className="max-w-4xl mx-auto text-center text-reveal">
          <h1 className="font-display text-5xl md:text-6xl font-bold text-primary mb-6">
            Exhibitions and more professional events
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            A chronicle of ceramic artistry showcased across national and international platforms
          </p>
        </div>
      </section>

      {/* Other Exhibitions Gallery */}
      <section className="container mx-auto px-4 mb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {galleryExhibitions.map((exhibition) => (
            <div key={exhibition.id} className="text-reveal">
              <div 
                className="overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer group relative aspect-[4/3]"
                onClick={() => {
                  setActiveGallery(exhibition);
                  setCurrentImageIndex(0);
                }}
              >
                {exhibition.images.length >= 4 ? (
                  <div className="grid grid-cols-2 grid-rows-2 gap-1 h-full">
                    {exhibition.images.slice(0, 4).map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Exhibition image ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ))}
                  </div>
                ) : exhibition.images.length >= 2 ? (
                  <div className="grid grid-cols-2 gap-1 h-full">
                    {exhibition.images.slice(0, 2).map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Exhibition image ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ))}
                  </div>
                ) : (
                  <img
                    src={exhibition.images[0]}
                    alt="Exhibition image"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white font-medium">
                    {exhibition.images.length > 1 ? `View Gallery (${exhibition.images.length})` : 'View Image'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Exhibition - Earthen Echoes */}
      <section className="container mx-auto px-4 mb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div 
              className="overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
              onClick={() => {
                setActiveGallery({
                  id: featuredExhibition.id,
                  images: featuredExhibition.images,
                  name: featuredExhibition.title,
                  location: featuredExhibition.location,
                  year: '2021',
                });
              }}
            >
              <div className="relative">
                <img
                  src={featuredExhibition.images[0]}
                  alt={featuredExhibition.title}
                  className="w-full h-auto object-contain group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white font-medium">View Gallery ({featuredExhibition.images.length})</span>
                </div>
              </div>
            </div>
            <div className="text-reveal">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-primary mb-4">
                {featuredExhibition.title}
              </h2>
              <p className="text-lg text-muted-foreground mb-2">{featuredExhibition.location}</p>
              <p className="text-muted-foreground mb-6">{featuredExhibition.date}</p>
              <div className="prose prose-lg text-foreground/80 space-y-4">
                {featuredExhibition.description.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Modal */}
      <Dialog open={!!activeGallery} onOpenChange={() => setActiveGallery(null)}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
          {activeGallery && (
            <div className="relative">
              <button
                onClick={() => setActiveGallery(null)}
                className="absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              <div className="flex items-center justify-center min-h-[60vh] p-8">
                {activeGallery.images.length > 1 && (
                  <button
                    onClick={prevImage}
                    className="absolute left-4 text-white/80 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                )}
                
                <img
                  src={activeGallery.images[currentImageIndex]}
                  alt={`Image ${currentImageIndex + 1}`}
                  className="max-h-[70vh] max-w-full object-contain"
                />
                
                {activeGallery.images.length > 1 && (
                  <button
                    onClick={nextImage}
                    className="absolute right-4 text-white/80 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                )}
              </div>
              
              <div className="text-center pb-6 text-white">
                {activeGallery.name && (
                  <h3 className="font-display text-xl font-semibold mb-2">
                    {activeGallery.name}
                  </h3>
                )}
                {activeGallery.location && (
                  <p className="text-white/70 mb-1">{activeGallery.location}</p>
                )}
                {activeGallery.images.length > 1 && (
                  <p className="text-sm text-white/50">
                    {currentImageIndex + 1} / {activeGallery.images.length}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Exhibitions;